import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, useTheme } from '@material-ui/core';

import { useStyles } from './UserActivityDashboard.styles';
import { UserActivityService } from '../../modules/api/userActivity';
import { DEFAULT_ACTIVITY } from './utils';
import { KpiCards, FilterBar, SessionsPanel, SessionActivityPanel } from './components';

const SESSIONS_PAGE_SIZE = 20;

export default function UserActivityDashboard(props) {
	const { appId } = props;
	const classes = useStyles();
	const theme = useTheme();

	const [selectedSessionId, setSelectedSessionId] = useState(null);
	const [dateRange, setDateRange] = useState('7');
	const [userFilter, setUserFilter] = useState([]);
	const [members, setMembers] = useState([]);
	const [membersLoading, setMembersLoading] = useState(false);
	const [userFilterAnchorEl, setUserFilterAnchorEl] = useState(null);

	const [kpiTotalSessions, setKpiTotalSessions] = useState(0);
	const [kpiActiveUsers, setKpiActiveUsers] = useState(0);
	const [kpiAvgDuration, setKpiAvgDuration] = useState(0);
	const [kpiTotalActions, setKpiTotalActions] = useState(0);
	const [kpiLoading, setKpiLoading] = useState(false);

	const [sessions, setSessions] = useState([]);
	const [sessionsPage, setSessionsPage] = useState(1);
	const [totalSessions, setTotalSessions] = useState(0);
	const [hasMoreSessions, setHasMoreSessions] = useState(false);
	const [sessionsLoading, setSessionsLoading] = useState(false);
	const [sessionsLoadingMore, setSessionsLoadingMore] = useState(false);
	const [listHeight, setListHeight] = useState(400);
	const sessionsListRef = useRef(null);

	const [activity, setActivity] = useState(DEFAULT_ACTIVITY);
	const [activityLoading, setActivityLoading] = useState(false);

	const days = Number(dateRange) || 7;
	const payload = { appId, days };
	const memberIdsFilter = Array.isArray(userFilter) && userFilter.length > 0 ? userFilter : undefined;

	const fetchKpis = useCallback(() => {
		if (!appId) return;
		setKpiLoading(true);
		Promise.all([
			UserActivityService.getTotalSessions(payload),
			UserActivityService.getActiveUsers({ appId }),
			UserActivityService.getAvgSessionDuration(payload),
			UserActivityService.getTotalActions(payload),
		])
			.then(([r1, r2, r3, r4]) => {
				if (r1?.data?.value != null) setKpiTotalSessions(r1.data.value);
				if (r2?.data?.value != null) setKpiActiveUsers(r2.data.value);
				if (r3?.data?.value != null) setKpiAvgDuration(r3.data.value);
				if (r4?.data?.value != null) setKpiTotalActions(r4.data.value);
			})
			.catch(() => {})
			.finally(() => setKpiLoading(false));
	}, [appId, days]);

	const fetchMembers = useCallback(() => {
		if (!appId) return;
		setMembersLoading(true);
		UserActivityService.getSessionMembers(payload)
			.then((res) => setMembers(res?.data?.members || []))
			.catch(() => setMembers([]))
			.finally(() => setMembersLoading(false));
	}, [appId, days]);

	const fetchSessions = useCallback((page = 1, append = false) => {
		if (!appId) return;
		if (page === 1) {
			setSessionsLoading(true);
			setSessions([]);
		} else {
			setSessionsLoadingMore(true);
		}
		UserActivityService.getSessions({
			...payload,
			memberIds: memberIdsFilter,
			page,
			pageSize: SESSIONS_PAGE_SIZE,
		})
			.then((res) => {
				const list = res?.data?.sessions || [];
				const total = res?.data?.total ?? 0;
				const hasMore = !!res?.data?.hasMore;
				setTotalSessions(total);
				setHasMoreSessions(hasMore);
				setSessionsPage(page);
				if (append) {
					setSessions((prev) => [...prev, ...list]);
				} else {
					setSessions(list);
					setSelectedSessionId((prev) => {
						if (!list.length) return null;
						if (prev && list.some((s) => s.id === prev)) return prev;
						return list[0].id;
					});
					if (!list.length) setActivity(DEFAULT_ACTIVITY);
				}
			})
			.catch(() => {
				if (!append) setSessions([]);
			})
			.finally(() => {
				setSessionsLoading(false);
				setSessionsLoadingMore(false);
			});
	}, [appId, days, memberIdsFilter]);

	const loadMoreSessions = useCallback(() => {
		if (sessionsLoading || sessionsLoadingMore || !hasMoreSessions) return;
		fetchSessions(sessionsPage + 1, true);
	}, [fetchSessions, sessionsPage, hasMoreSessions, sessionsLoading, sessionsLoadingMore]);

	const fetchSessionDetails = useCallback(() => {
		if (!appId || !selectedSessionId) {
			setActivity(DEFAULT_ACTIVITY);
			return;
		}
		setActivityLoading(true);
		UserActivityService.getSessionDetails({ appId, sessionId: selectedSessionId })
			.then((res) => {
				const timeline = res?.data?.timeline || [];
				setActivity(timeline.length ? timeline : DEFAULT_ACTIVITY);
			})
			.catch(() => setActivity(DEFAULT_ACTIVITY))
			.finally(() => setActivityLoading(false));
	}, [appId, selectedSessionId]);

	useEffect(() => { fetchKpis(); }, [fetchKpis]);
	useEffect(() => { fetchMembers(); }, [fetchMembers]);
	useEffect(() => { fetchSessions(1); }, [fetchSessions]);
	useEffect(() => { fetchSessionDetails(); }, [fetchSessionDetails]);

	useEffect(() => {
		const el = sessionsListRef.current;
		if (!el) return;
		const measure = () => setListHeight(el.clientHeight || 400);
		measure();
		const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
		if (ro) ro.observe(el);
		return () => { if (ro && el) try { ro.unobserve(el); } catch (_) {} };
	}, [sessionsLoading]);

	const selectedSession = sessions.find((s) => s.id === selectedSessionId);

	return (
		<Box className={classes.root}>
			<KpiCards
				classes={classes}
				loading={kpiLoading}
				totalSessions={kpiTotalSessions}
				activeUsers={kpiActiveUsers}
				avgDuration={kpiAvgDuration}
				totalActions={kpiTotalActions}
			/>

			<FilterBar
				classes={classes}
				theme={theme}
				dateRange={dateRange}
				onDateRangeChange={setDateRange}
				userFilter={userFilter}
				onUserFilterAnchor={setUserFilterAnchorEl}
				userFilterAnchorEl={userFilterAnchorEl}
				onUserFilterClose={() => setUserFilterAnchorEl(null)}
				onUserFilterSave={setUserFilter}
				members={members}
				membersLoading={membersLoading}
			/>

			<Box className={classes.mainContent}>
				<SessionsPanel
					classes={classes}
					sessions={sessions}
					selectedSessionId={selectedSessionId}
					onSelectSession={setSelectedSessionId}
					sessionsLoading={sessionsLoading}
					sessionsLoadingMore={sessionsLoadingMore}
					hasMoreSessions={hasMoreSessions}
					listHeight={listHeight}
					listRef={sessionsListRef}
					onLoadMore={loadMoreSessions}
				/>

				<SessionActivityPanel
					classes={classes}
					selectedSession={selectedSession}
					activity={activity}
					activityLoading={activityLoading}
				/>
			</Box>
		</Box>
	);
}
